import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {defer, map, type Subscription} from "rxjs"
import type {CompleteMeshData} from "#template/interfaces/mesh-data"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {LodTypeValue} from "#template/value-types"
import type {DataObject, DataObjectWithRelated} from "@cm/utils"
import type {DataNodes} from "@cm/render-nodes"
import {getMeshGraphInfo} from "#template/utils/render-graph-info"

const TD = TypeDescriptors

export type MeshMetaDataItem = {
    dataObjectReference: DataNodes.DataObjectReference
    resolvedData: DataObject
}

const getMeshMetaDataDescriptor: {
    sceneManager: InletDescriptor<ISceneManager>
    lodType: InletDescriptor<LodTypeValue>
    completeMeshData: InletDescriptor<CompleteMeshData>
    meshMetaData: OutletDescriptor<MeshMetaDataItem[]>
} = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    lodType: TD.inlet(TD.Identity<LodTypeValue>()),
    completeMeshData: TD.inlet(TD.CompleteMeshData),
    meshMetaData: TD.outlet(TD.Array(TD.Identity<MeshMetaDataItem>())),
}

export class GetMeshMetaData implements NodeClassImpl<typeof getMeshMetaDataDescriptor, typeof GetMeshMetaData> {
    static descriptor = getMeshMetaDataDescriptor
    static uniqueName = "GetMeshMetaData"
    sceneManager!: Inlet<ISceneManager>
    lodType!: Inlet<LodTypeValue>
    completeMeshData!: Inlet<CompleteMeshData>
    meshMetaData!: Outlet<MeshMetaDataItem[]>

    private pending: Subscription | null = null

    run() {
        if (this.sceneManager === NotReady || this.lodType === NotReady || this.completeMeshData === NotReady) {
            this.meshMetaData.emitIfChanged(NotReady)
            return
        }

        const {sceneManager, completeMeshData, lodType} = this

        const {dataObjectReferences} = getMeshGraphInfo(
            lodType === "pathTraced" ? completeMeshData.abstract.renderGeometryGraph : completeMeshData.abstract.displayGeometryGraph,
        )

        this.pending = this.sceneManager.addTask({
            description: `getMeshMetaData(${this.completeMeshData.reified.contentHash})`,
            task: defer(() =>
                Promise.all(
                    dataObjectReferences.map(async (dataObjectReference) => {
                        const resolvedData = await (async (): Promise<DataObjectWithRelated> => {
                            const resolveDataObject = await sceneManager.getResolver().dataObjectLegacy(dataObjectReference.dataObjectId)

                            const {size} = resolveDataObject

                            if (size === undefined || size === null) {
                                const size = await sceneManager.getDataObjectSize(resolveDataObject)
                                return {
                                    ...resolveDataObject,
                                    size,
                                }
                            } else {
                                return resolveDataObject
                            }
                        })()

                        return {
                            dataObjectReference,
                            resolvedData,
                        }
                    }),
                ),
            ).pipe(
                map((resolvedData) => {
                    this.meshMetaData.emitIfChanged(resolvedData)
                }),
            ),
            critical: false,
        })
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
